<script>
  import { base64DataURLToBlob, scaleImage } from '@/utils/misc'
  import VueCropper from 'vue-cropperjs'

  export default {
    name: 'UploadYoursBrowser',
    components: {
      TrashIcon: () => import('vue-feather-icons/icons/TrashIcon'),
      FileUploadArea: () =>
        import('@/components/Elements/FileInputs/FileUploadArea'),
      BrowserTemplate: () => import('./BrowserTemplate'),
      VueCropper,
    },
    props: {
      activeImage: String,
      formType: {
        type: String,
        default: null // 'static' or 'interactive'
      }
    },
    data: () => ({
      uploadedCoversList: [],
      showCropper: false,
      cropperImage: null, // base64
      cropperFile: null, // original file
      cropperLoading: false,
      cropBoxData: null, // para guardar el estado del cropper si se requiere
    }),
    computed: {
      // Detectar el tipo de formulario desde el store si no se pasa como prop
      detectedFormType() {
        if (this.formType) return this.formType;
        
        // Intentar detectar desde el store
        try {
          if (this.$store.getters['edit/isFormStatic']) return 'static';
          if (this.$store.getters['edit/isFormInteractive']) return 'interactive';
        } catch (e) {
          // Si no hay store disponible, usar defecto
        }
        
        return 'static'; // Defecto
      },
      
      // Aspect ratio específico según el tipo de formulario
      aspectRatio() {
        return this.detectedFormType === 'interactive' ? 612/765 : 256/65;
      },
      
      // Dimensiones de texto para mostrar
      dimensionsText() {
        return this.detectedFormType === 'interactive' ? '612:765 (Portrait)' : '256:65 (Banner)';
      }
    },
    beforeCreate() {
      this.StorageActions.listPublicObjects('form_covers/_').then(list => {
        if (list)
          this.uploadedCoversList = list
            .sort(({ LastModified: a }, { LastModified: b }) => b - a)
            .map(({ Key: relativePath }) => ({
              url: this.constants.S3_BASE_URL + relativePath,
            }))
      })
    },
    methods: {
      setActiveImage(image) {
        if (image.url) {
          if (image.url === this.activeImage) this.$emit('set-active-image', '')
          else this.$emit('set-active-image', image.url)
        }
      },
      inputCover(file) {
        // Si file es un array, toma el primero
        if (Array.isArray(file)) {
          file = file[0];
        }
        if (!file) return;

        // Crear URL temporal del archivo
        this.cropperImage = URL.createObjectURL(file);
        this.cropperFile = file;
        this.showCropper = true;
      },
      async confirmCrop() {
        this.cropperLoading = true;
        
        // Obtener canvas recortado manteniendo las proporciones del usuario
        const canvas = this.$refs.pictureCropper.getCroppedCanvas({
          maxWidth: 1920, // Resolución máxima pero manteniendo proporciones
          maxHeight: 1080,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        });
        
        if (!canvas) {
          this.cropperLoading = false;
          return;
        }
        
        // Convertir canvas a blob
        canvas.toBlob(async (blob) => {
          if (!blob) {
            this.cropperLoading = false;
            return;
          }
          
          // Crear versión optimizada manteniendo las proporciones del recorte
          const dataUrl = await this.Utils.blobToBase64Data(blob);
          // Escalar manteniendo las proporciones originales del recorte
          const scaledFile = await scaleImage(dataUrl, 1920, 1920) // Escalar manteniendo proporciones
            .then(dataUrl => base64DataURLToBlob(dataUrl));
          
          let hash = this.Utils.generateUUID();
          this.uploadedCoversList.splice(0, 0, { hash });
          
          this.StorageActions.putPublicObject({
            file: scaledFile,
            filePath: 'form_covers/_' + hash,
          })
            .then(({ fileUrl }) => {
              this.uploadedCoversList.find(({ hash: h }) => h === hash).url = fileUrl;
              if (this.cropperImage) {
                URL.revokeObjectURL(this.cropperImage);
              }
              this.showCropper = false;
              this.cropperImage = null;
              this.cropperFile = null;
              this.cropperLoading = false;
            });
        }, 'image/jpeg', 0.9); // Usar JPEG con buena calidad
      },
      cancelCrop() {
        if (this.cropperImage) {
          URL.revokeObjectURL(this.cropperImage); // Limpiar URL temporal
        }
        this.showCropper = false
        this.cropperImage = null
        this.cropperFile = null
      },
      removeCover(coverIndex) {
        let [removedImage] = this.uploadedCoversList.splice(coverIndex, 1)
        const relativePath = this.constants.GET_S3_OBJECT_RELATIVE_PATH_BY_URL(
          removedImage.url
        )
        const splittedPath = relativePath.split('/')
        const folderPath =
          splittedPath.slice(0, splittedPath.length - 1).join('/') + '/'
        const currentImageName = splittedPath[splittedPath.length - 1]
        this.StorageActions.moveObject(
          relativePath,
          folderPath + currentImageName.slice(1)
        ).then(({ currentPath }) => {
          if (removedImage.url === this.activeImage)
            this.$emit(
              'set-active-image:active-url-replaced',
              this.constants.S3_BASE_URL + currentPath
            )
        })
      },
    },
  }
</script>

<template>
  <BrowserTemplate>
    <v-row>
      <v-col
        align-self="center"
        cols="12"
        sm="6"
      >
        <div class="upload-yours-cover-area-wrapper">
          <FileUploadArea
            vertical
            :max-file-size="10"
            :max-files-count="1"
            :accept-mime="['image/jpeg', 'image/png']"
            @input="inputCover"
          >
            <template #header>Drop Image or choose file...</template>
            <template #subheader>Max size 10mb</template>
          </FileUploadArea>
        </div>
      </v-col>
      <v-col
        v-for="(image, index) in uploadedCoversList"
        :key="image.url ? image.url.toLocaleString() : index"
        align-self="center"
        cols="12"
        sm="6"
      >
        <v-hover v-slot="{ hover }">
          <v-img
            class="rounded-lg result-image"
            width="288px"
            height="162px"
            :class="activeImage === image.url && 'active'"
            :src="image.url"
            @load="image.loaded = true"
            @click="setActiveImage(image)"
          >
            <template #default>
              <v-scale-transition origin="center">
                <v-btn
                  v-if="hover && image.loaded"
                  class="image-floating-remove-button v-btn--error"
                  icon
                  small
                  @click.stop="removeCover(index)"
                >
                  <TrashIcon size="18" />
                </v-btn>
              </v-scale-transition>
            </template>
            <template #placeholder>
              <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
              >
                <f-circle-loader size="5" />
              </v-row>
            </template>
          </v-img>
        </v-hover>
      </v-col>
    </v-row>
    <v-dialog v-model="showCropper" :max-width="$vuetify.breakpoint.mobile ? '100%' : '900px'" :fullscreen="$vuetify.breakpoint.mobile" persistent>
      <v-card>
        <v-card-title>
                      <span>Crop image for header</span>
          <v-spacer />
                      <span class="caption text--secondary">Adjust the area to look perfect in your form</span>
        </v-card-title>
        <v-card-text>
          <vue-cropper
            v-if="cropperImage"
            ref="pictureCropper"
            :src="cropperImage"
            :aspect-ratio="aspectRatio"
            :view-mode="1"
            :auto-crop-area="1"
            :guides="true"
            :center="true"
            :highlight="true"
            :crop-box-movable="true"
            :crop-box-resizable="true"
            :toggle-drag-mode-on-dblclick="false"
            :style="`width:100%;height:${$vuetify.breakpoint.mobile ? '300px' : '500px'};`"
          />
          <div class="mt-3">
            <span class="body-2">{{ dimensionsText }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="gray_100" rounded @click="cancelCrop" :disabled="cropperLoading">Cancel</v-btn>
          <v-btn color="blue_400" rounded @click="confirmCrop" :loading="cropperLoading">Confirm Crop</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </BrowserTemplate>
</template>

<style lang="scss">
  .upload-yours-cover-area-wrapper {
    height: 160px;
    padding: 3px;
  }

  .image-floating-remove-button {
    position: absolute;
    background-color: white;
    bottom: 10px;
    right: 10px;
  }

  .result-image {
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    &.active {
      border: 3px solid #2196F3;
      box-shadow: 0 4px 20px rgba(33, 150, 243, 0.3);
    }
  }

  // Mejorar el cropper
  .cropper-container {
    .cropper-view-box {
      outline: 2px solid #2196F3;
      outline-offset: -1px;
    }
    
    .cropper-face {
      background-color: rgba(33, 150, 243, 0.1);
    }
  }

</style>
